import React, { useMemo, useState } from 'react';
import { PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import type { PartnerCategoryId } from '../../types/partners';
import { ACTIVE_PARTNER_CATEGORIES, PUBLISHED_PARTNERS, categoryLabel } from '../../data/partners';
import { usePartnerVotes } from '../../contexts/PartnerVotesContext';
import { PartnerCard } from './PartnerCard';
import { cn } from '../../src/lib/utils';
type SortKey = 'featured' | 'upvotes' | 'recent' | 'alpha';
const SORTS: {
  key: SortKey;
  label: string;
}[] = [{
  key: 'featured',
  label: 'Featured'
}, {
  key: 'upvotes',
  label: 'Most Upvoted'
}, {
  key: 'recent',
  label: 'Recently Added'
}, {
  key: 'alpha',
  label: 'Alphabetical'
}];
export function PartnerDirectory({
  query,
  onQueryChange
}: {
  query: string;
  onQueryChange: (value: string) => void;
}) {
  const [category, setCategory] = useState<PartnerCategoryId | 'all'>('all');
  const [sort, setSort] = useState<SortKey>('featured');
  const {
    counts,
    status
  } = usePartnerVotes();
  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = PUBLISHED_PARTNERS.filter(partner => {
      if (category !== 'all' && !partner.categories.includes(category)) return false;
      if (!needle) return true;
      const haystack = [partner.name, partner.tagline, partner.shortDescription, partner.fullDescription, ...partner.benefits, ...partner.categories.map(categoryLabel)].join(' ').toLowerCase();
      return haystack.includes(needle);
    });
    const byFeatured = (a: (typeof filtered)[number], b: (typeof filtered)[number]) => Number(b.featured) - Number(a.featured);
    return [...filtered].sort((a, b) => {
      if (sort === 'upvotes') return (counts[b.slug] ?? 0) - (counts[a.slug] ?? 0) || byFeatured(a, b);
      if (sort === 'recent') return b.addedAt.localeCompare(a.addedAt);
      if (sort === 'alpha') return a.name.localeCompare(b.name);
      return byFeatured(a, b) || a.name.localeCompare(b.name);
    });
  }, [query, category, sort, counts]);
  return <section aria-labelledby="directory-heading" className="mx-auto w-full max-w-[1320px] px-4 py-14 sm:px-6 lg:px-8 lg:py-16" data-id="element-925" data-mp="25v5veybt6c98">
      <h2 id="directory-heading" className="sr-only" data-id="element-926" data-mp="2aquyhbn1ebaa">
        Partner directory
      </h2>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-927" data-mp="1wvaj2q6iob7m">
        <div role="group" aria-label="Filter by category" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0" data-id="element-928" data-mp="21qg47ms3hlun">
          {[{
          id: 'all' as const,
          label: 'All Partners'
        }, ...ACTIVE_PARTNER_CATEGORIES].map(item => {
          const active = category === item.id;
          return <button key={item.id} type="button" aria-pressed={active} onClick={() => setCategory(item.id)} className={cn('inline-flex min-h-[40px] shrink-0 items-center rounded-full border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2', active ? 'border-lime bg-lime text-ink' : 'border-hairline bg-panel text-text-secondary hover:border-lime/40 hover:text-text-primary')} data-id="element-929" data-mp="1i40gqmhltq0i">
                {item.label}
              </button>;
        })}
        </div>
        <div className="flex shrink-0 items-center gap-2" data-id="element-930" data-mp="18fwbm3fxb2an">
          <label htmlFor="partner-sort" className="text-sm text-text-secondary" data-id="element-931" data-mp="18ebfi9dynbao">
            Sort by
          </label>
          <select id="partner-sort" value={sort} onChange={event => setSort(event.target.value as SortKey)} className="min-h-[40px] rounded-lg border border-hairline bg-panel px-3 text-sm font-medium text-text-primary focus:border-lime focus:outline-none focus:ring-2 focus:ring-lime/30" data-id="element-932" data-mp="1i67seqkph5yy">
            {SORTS.map(option => <option key={option.key} value={option.key} data-id="element-933" data-mp="3goay02ap2q2t">
                {option.label}
              </option>)}
          </select>
        </div>
      </div>

      <p aria-live="polite" className="mt-6 text-sm text-text-tertiary" data-id="element-934" data-mp="3lljii9jbf678">
        {results.length} {results.length === 1 ? 'partner' : 'partners'}
        {sort === 'upvotes' && status !== 'ready' ? ' · Vote counts appear once partner voting opens' : ''}
      </p>

      {results.length > 0 ? <ul className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-935" data-mp="2xr9qoato4dvp">
          {results.map(partner => <li key={partner.slug} data-id="element-936" data-mp="32qmcqtcptgx8">
              <PartnerCard partner={partner} data-id="element-937" data-mp="37n3ckkta1jq5" />
            </li>)}
          {category === 'all' && !query ? <li data-id="element-938" data-mp="3bq9clapsqeo6">
              <a href="#become-a-partner" className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-hairline bg-surface p-6 text-center transition-colors hover:border-lime/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-939" data-mp="3bqri6xycm58r">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-panel text-lime shadow-sm" data-id="element-940" data-mp="27ewkwogk7o8c">
                  <PlusIcon className="h-5 w-5" aria-hidden="true" data-id="element-941" data-mp="22nobvtj564vp" />
                </span>
                <span className="text-base font-semibold text-text-primary" data-id="element-942" data-mp="1xqekw9g3tl5r">More partners are joining</span>
                <span className="max-w-[260px] text-sm text-text-secondary" data-id="element-943" data-mp="1srl68dp2zo40">Build tools for founders, investors or innovation teams? Apply to be listed.</span>
              </a>
            </li> : null}
        </ul> : <div className="mt-4 flex flex-col items-center rounded-2xl border border-hairline bg-surface px-6 py-14 text-center" data-id="element-944" data-mp="1jry6rxfwwsdl">
          <SearchIcon className="h-6 w-6 text-text-tertiary" aria-hidden="true" data-id="element-945" data-mp="3e3layakp256n" />
          <p className="mt-3 text-base font-semibold text-text-primary" data-id="element-946" data-mp="3e1ilbrngee2g">No partners match your search yet.</p>
          <p className="mt-1 text-sm text-text-secondary" data-id="element-947" data-mp="3s9ey7igwzxjh">Try a different term or category — or tell us what you’d like to see.</p>
          <button type="button" onClick={() => {
        onQueryChange('');
        setCategory('all');
      }} className="mt-5 inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-hairline bg-panel px-4 text-sm font-semibold text-text-primary hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-948" data-mp="uzbjac7m4rn">
            <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-949" data-mp="jsaep60ypicd" />
            Clear filters
          </button>
        </div>}
    </section>;
}